/* eslint-disable react/jsx-key */
import {
  lit,
  end,
  zero,
  parse,
  Route,
  Parser,
  format,
  str,
  then,
  Formatter,
} from "fp-ts-routing";
import * as t from "io-ts";
import React, { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { NumberFromString } from "io-ts-types/lib/NumberFromString";
import GlobalErrorBoundary from "ui-components/global-error-boundary";
import NotFound from "./pages/not-found";

import SignUp from "./components/shared/sign-up";
import SignIn from "./components/shared/sign-in";
import ReviewPage from "./pages/review";
import TaskPage, {TaskPageSubTabIdIO } from "./pages/task";
import UnderDevelopment from "./components/shared/under-development";
import getDefaultReviewProps from "./pages/review/get-default-props";
import useSession from "ui-io/use-session";
import ConfigurationLayout from "./components/configuration-layout";
import ConfigurationUpload from "./pages/configuration/upload";
import ConfigurationCreate from "./pages/configuration/create";
import { queryPartial } from "./utils/query-partial";
import TaskLayout from "./components/task-layout";
import TaskInbox, { TaskPanelProps } from "./pages/tasks";
import { InboxViewIO } from "./pages/tasks/inbox-views";

import { SortOrderIO } from "ui-io/task-io";

const configurationQuery = t.partial({
  companyID: t.string,
});

const taskPageQuery = t.union([
  t.partial({
    tabId: InboxViewIO,
    subtabId: TaskPageSubTabIdIO,
  }),
  t.type({
    // tabId: t.literal("Inbox"),
    subtabId: t.literal("company"),
  }),
]);

const apiDocs = lit("api").then(lit("docs"));

const def = end;
const signUp = lit("sign-up").then(end);
const signIn = lit("sign-in").then(end);
// const task = lit("task").then(end);
const tasks = lit("tasks").then(end);
// const taskInboxQuery =
//   t.union([
//     t.type({
//       page: t.number,
//       view: t.string,
//       sortBy: t.string,
//       sortOrder: t.string,
//     }),
//     t.partial({
//       assignee: t.string,
//       activeFilterString: t.string,
//     })
//   ]);

const taskInboxQuery = t.partial({
  view: t.string,
  page: NumberFromString,
  assignee: t.string,
  sortBy: t.string,
  sortOrder: SortOrderIO,
  activeFilterString: t.string,
  // filter: t.string,
  // workstreamIds: t.string,
  // tags: t.string,
  // taskTypes: t.string,
  // text: t.string,
  // priority: t.string,
});

// const task = lit("tasks").then(
//   lit("inbox")
const task = lit("task")
  .then(str("taskId"))
  .then(queryPartial(taskPageQuery))
  .then(end);

const taskInbox = lit("tasks")
  .then(lit("inbox"))
  .then(queryPartial(taskInboxQuery))
  .then(end);
const taskInProgress = lit("tasks")
  .then(lit("in-progress"))
  .then(queryPartial(taskInboxQuery))
  .then(end);
const taskApproved = lit("tasks")
  .then(lit("approved"))
  .then(queryPartial(taskInboxQuery))
  .then(end);
const taskPeerReview = lit("tasks")
  .then(lit("peer-review"))
  .then(queryPartial(taskInboxQuery))
  .then(end);
const taskPending = lit("tasks")
  .then(lit("pending"))
  .then(queryPartial(taskInboxQuery))
  .then(end);
const taskRejected = lit("tasks")
  .then(lit("rejected"))
  .then(queryPartial(taskInboxQuery))
  .then(end);

const review = lit("review").then(end);
const analytics = lit("analytics").then(end);
const configurationCreate = lit("configurations").then(
  lit("create").then(queryPartial(configurationQuery)).then(end)
);
const configurationUpload = lit("configurations").then(lit("upload").then(end));

const match = {
  default: def,

  signUp,
  signIn,
  review,
  tasks,
  task,
  taskInbox,
  taskApproved,
  taskPeerReview,
  taskPending,
  taskInProgress,
  taskRejected,
  analytics,
  configurationCreate,
  configurationUpload,
  apiDocs,
};

export type MatchT = keyof typeof match;

const withAuthentication = (
  router: Parser<ReactElement>
): Parser<ReactElement> => {
  return router
    .map((component) => {
      const { isAuthenticated } = useSession();
      // Exclude the sign-in route from authentication checks
      if (component.type === SignUp) {
        return component;
      } else if (component.type === SignIn) {
        if (isAuthenticated) {
          return (
            <Navigate to={formatLocation.review(getDefaultReviewProps())} />
          );
        } else {
          return component;
        }
      }
      return isAuthenticated ? (
        component
      ) : (
        <Navigate to={formatLocation.signIn({})} />
      );
    })
    .map((x) => (
      <>
        <ScrollToTop />
        <GlobalErrorBoundary>{x}</GlobalErrorBoundary>
      </>
    ));
};

const router = withAuthentication(
  zero<ReactElement>()
    .alt(
      match.default.parser.map(() => {
        const { isAuthenticated } = useSession();
        return (
          <Navigate
            to={
              isAuthenticated
                ? formatLocation.taskInbox({})
                : formatLocation.signIn({})
            }
          />
        );
      })
    )
    .alt(match.signUp.parser.map((props) => <SignUp {...props} />))
    .alt(match.signIn.parser.map((props) => <SignIn {...props} />))

    // .alt(match.task.parser.map((props) => <TaskLayout> <TaskPage {...props} /></TaskLayout>))
    // .alt(
    //   match.tasks.parser.map(() => (
    //     <Navigate to={formatLocation.taskInbox({})} />
    //   ))
    // )
    // .alt(
    //   match.taskPending.parser.map((props) => (
    //     <TaskLayout>
    //       <TaskPending {...props} />
    //     </TaskLayout>
    //   ))
    // )
    .alt(
      match.taskPending.parser.map((props) => (
        <TaskLayout>
          <TaskInbox
            {...{
              ...props,
              page: props.page ?? 1,
              view: "Pending",
              sortBy: (props.sortBy as TaskPanelProps["sortBy"]) ?? "assignee",
              sortOrder:
                (props.sortOrder as TaskPanelProps["sortOrder"]) ?? "ASC",
            }}
          />
        </TaskLayout>
      ))
    )
    .alt(
      match.taskInbox.parser.map((props) => (
        <TaskLayout>
          <TaskInbox
            {...{
              ...props,
              page: props.page ?? 1,
              view: "Inbox",
              sortBy: (props.sortBy as TaskPanelProps["sortBy"]) ?? "assignee",
              sortOrder:
                (props.sortOrder as TaskPanelProps["sortOrder"]) ?? "ASC",
            }}
          />
        </TaskLayout>
      ))
    )
    .alt(match.task.parser.map((props) => <TaskPage {...props} />))
    .alt(
      match.taskInProgress.parser.map((props) => (
        <TaskLayout>
          <TaskInbox
            {...{
              ...props,
              page: props.page ?? 1,
              view: "InProgress",
              sortBy: (props.sortBy as TaskPanelProps["sortBy"]) ?? "assignee",
              sortOrder:
                (props.sortOrder as TaskPanelProps["sortOrder"]) ?? "ASC",
            }}
          />
        </TaskLayout>
      ))
    )
    .alt(
      match.taskApproved.parser.map((props) => (
        <TaskLayout>
          <TaskInbox
            {...{
              ...props,
              page: props.page ?? 1,
              view: "Approved",
              sortBy: (props.sortBy as TaskPanelProps["sortBy"]) ?? "assignee",
              sortOrder:
                (props.sortOrder as TaskPanelProps["sortOrder"]) ?? "ASC",
            }}
          />
        </TaskLayout>
      ))
    )

    .alt(
      match.taskPeerReview.parser.map((props) => (
        <TaskLayout>
          <TaskInbox
            {...{
              ...props,
              page: props.page ?? 1,
              view: "InReview",
              sortBy: (props.sortBy as TaskPanelProps["sortBy"]) ?? "assignee",
              sortOrder:
                (props.sortOrder as TaskPanelProps["sortOrder"]) ?? "ASC",
            }}
          />
        </TaskLayout>
      ))
    )
    .alt(
      match.taskRejected.parser.map((props) => (
        <TaskLayout>
          <TaskInbox
            {...{
              ...props,
              page: props.page ?? 1,
              view: "Rejected",
              sortBy: (props.sortBy as TaskPanelProps["sortBy"]) ?? "assignee",
              sortOrder:
                (props.sortOrder as TaskPanelProps["sortOrder"]) ?? "ASC",
            }}
          />
        </TaskLayout>
      ))
    )
    // .alt(match.review.parser.map((props) => <ReviewPage {...props} />))
    .alt(match.review.parser.map((props) => <UnderDevelopment />))
    .alt(match.analytics.parser.map((props) => <UnderDevelopment />))
    .alt(
      match.configurationCreate.parser.map((props) => (
        <ConfigurationLayout>
          <ConfigurationCreate {...props} />
        </ConfigurationLayout>
      ))
    )
    .alt(
      match.configurationUpload.parser.map((props) => (
        <ConfigurationLayout>
          <ConfigurationUpload {...props} />
        </ConfigurationLayout>
      ))
    )

    .alt(Parser.of(<NotFound />))
);

const ScrollToTop = () => {
  // const history = useHistory()
  React.useEffect(() => {
    // if (history.action === 'PUSH') {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    // }
  }, []);
  return null;
};

// Parse location
export const parseLocation = (s: string) =>
  parse(router, Route.parse(s), <NotFound />);

export const formatLocation = Object.entries(match).reduce(
  (acc, [x, y]) => ({
    ...acc,
    // @ts-ignore
    [x]: (props: any) => format<any>(y.formatter as Formatter<any>, props),
  }),
  {} as {
    [key in keyof typeof match]: (props: (typeof match)[key]["_A"]) => string;
  }
);

// const badTaskFormatter = formatLocation.task
// formatLocation.task = (props: Parameters<typeof badTaskFormatter>[0]) =>
//   cleanTaskUrl(badTaskFormatter(props))
